import React from 'react';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import TableData from '../../custom/Table/TableData';
import CellType from '../../../constants/type';
import useI18n from '../../../hooks/useI18n';
import useUserInfo from '../../../hooks/useUserInfo';
import { basketState } from '../../../state/basketState';
import { recommendationsState } from '../../../state/recommendationsState';
import { useRecoilState } from "recoil";
import { Alert, Divider, FormControlLabel, Popover, Switch, TextField, Tooltip } from '@mui/material';
import ScaleIcon from '@mui/icons-material/Scale';
import WbIridescentIcon from '@mui/icons-material/WbIridescent';
import basketService from '../../../api/basket';
import recommendationsService from '../../../api/recommendations';
import ConfirmDialog from '../../custom/ConfirmDialog/ConfirmDialog';
import InfoDialog from '../../custom/InfoDialog/InfoDialog';
import DetailScreen from '../../custom/DetailScreen/DetailScreen';
import moment from 'moment';
import { isCheckQuant, orderlimit, currencySignPrice1, compDetailUrl, appCompno } from '../../../config';
import getEffectiveOrderQty from '../../../helpers/getEffectiveOrderQty';

function BasketPage() {

    const { lang, l } = useI18n();
    //const [basket, setBasket] = React.useState([]);
    const [basketRecoil, setBasketRecoil] = useRecoilState(basketState);
    const [openConfirmDialog, setOpenConfirmDialog] = React.useState(false);
    const [openAlertDialog, setOpenAlertDialog] = React.useState(false);
    const [openSuccessDialog, setOpenSuccessDialog] = React.useState(false);
    const [rate, setRate] = React.useState(0);
    const { user } = useUserInfo();
    const [comment, setComment] = React.useState('');
    const [titelInfoDialog, setTitelInfoDialog] = React.useState(null);
    const [contentTextInfoDialog, setContentTextInfoDialog] = React.useState(null);
    
    const [openDetail, setOpenDetail] = React.useState(false);
    const [detailUrl, setDetailUrl] = React.useState('');
    const [showRecommendations, setShowRecommendations] = useRecoilState(recommendationsState);
    const [recommendations, setRecommendations] = React.useState([]);
    const [dismissedArtcodes, setDismissedArtcodes] = React.useState(new Set());
    const recSwitchRef = React.useRef(null);
    const [recInfoAnchorEl, setRecInfoAnchorEl] = React.useState(null);

    React.useEffect(() => {
        const seen = localStorage.getItem('basket_rec_info_seen');
        if (!seen && recSwitchRef.current) {
            const timer = setTimeout(() => setRecInfoAnchorEl(recSwitchRef.current), 400);
            return () => clearTimeout(timer);
        }
    }, []);

    const handleRecInfoClose = () => {
        localStorage.setItem('basket_rec_info_seen', '1');
        setRecInfoAnchorEl(null);
    };

    const GetBasket = async () => {
        try {
            const basket = await basketService.findAllByUserId(user.CODE, lang);

            setRate(basket?.math[0]?.FRRATE);
            setOpenAlertDialog(!basket?.math?.find((item) => item.QUANT < item.FMINQTY))
            setBasketRecoil({ basket });
        } catch (error) {
            console.error('Error findAllByUserId >> ', error);

        }
    };

    React.useEffect(() => {
        setBasketRecoil({ basket: {} });
        if (user) GetBasket();
    }, [user]);

    // Рекомендації завантажуються один раз: або при появі корзини (якщо перемикач вже увімкнений),
    // або при вмиканні перемикача (якщо корзина вже завантажена).
    // Зміни корзини після додавання товарів НЕ викликають повторний запит.
    const recsFetchedRef = React.useRef(false);
    React.useEffect(() => {
        if (!showRecommendations) {
            setRecommendations([]);
            recsFetchedRef.current = false;
            return;
        }
        const math = basketRecoil?.basket?.math;
        if (!math?.length || recsFetchedRef.current) return;
        recsFetchedRef.current = true;
        recommendationsService.getByBasket()
            .then(recs => setRecommendations(recs || []))
            .catch(err => console.error('Error fetching recommendations:', err));
    }, [showRecommendations, basketRecoil?.basket?.math]);

    // Рекомендовані товари знизу: 1 на категорію LEVEL1, відсортовані за LEVEL1, з розділювачем.
    const displayRows = React.useMemo(() => {
        const math = basketRecoil?.basket?.math;
        if (!math?.length || !showRecommendations || !recommendations.length) return math;

        const mathByArtcode = new Map(math.map(m => [m.QTARTCODE, m]));

        // Вибираємо найкращого кандидата (за total_score) для кожної категорії LEVEL1
        const byCategory = new Map();
        for (const rec of recommendations) {
            const artcode = rec.QTARTCODE;
            if (dismissedArtcodes.has(artcode)) continue;
            if (mathByArtcode.has(artcode)) continue;
            const cat = rec.LEVEL1 || '';
            const existing = byCategory.get(cat);
            if (!existing || rec.total_score > existing.total_score) {
                byCategory.set(cat, rec);
            }
        }

        const sortedCats = [...byCategory.keys()].sort();
        const ghostRows = sortedCats.map(cat => {
            const rec = byCategory.get(cat);
            const artcode = rec.QTARTCODE;
            const suggestedQty = getEffectiveOrderQty(rec);
            const finstockNum = parseFloat(rec.FINSTOCK);
            const inStock = isNaN(finstockNum) ? 999 : finstockNum;
            return {
                ...rec,
                _ghost: true,
                id: `_ghost_${artcode}`,
                QUANT: suggestedQty,
                inStock,
                ITEMSUM: 0,
                SUMFWEIGHT: 0,
                SUMFVOLUME: 0,
                FRRATE: rate,
            };
        });

        if (!ghostRows.length) return math;

        const divider = { _divider: true, id: '_rec_divider' };
        return [...math, divider, ...ghostRows];
    }, [basketRecoil?.basket?.math, showRecommendations, recommendations, dismissedArtcodes, rate]);

    // Товари, кількість яких не кратна FMINQTY (мін. замовлення) - заборонити оформлення замовлення
    const hasInvalidMultiplicity = React.useMemo(() => {
        const math = basketRecoil?.basket?.math;
        if (!math?.length) return false;

        return math.some((item) => {
            const effectiveQty = getEffectiveOrderQty(item);
            return effectiveQty > 1 && item.QUANT % effectiveQty !== 0;
        });
    }, [basketRecoil?.basket?.math]);

    const handleDelete = () => {

        const fetcData = async () => {

            try {
                await basketService.deleteBasket();
                clearBasket();
                setShowRecommendations(true);
            } catch (error) {
                console.error('Error deleteBasket >> ', error);
            }
        };

        fetcData();

        setOpenConfirmDialog(false)
    };

    const handleConfirm = () => {

        setBasketRecoil ({...basketState, ...{math: []}});
        const fetcData = async () => {

            try {
                const data = { ...basketRecoil.basket, comment };
                const res = await basketService.confirmBasket(data);

                if (res?.itemOutStock) {

                    var newMath = [];

                    for (let i = 0; i < data.math.length; i++) {
                        let element = data.math[i];
                        newMath.push({...element});
                        const itemStock = res?.basketWithStock.find(item => item.QTARTCODE === element.QTARTCODE);
                        const accessItemQty = (itemStock.INSTOCKSUM || 0) - (itemStock.RESERVEDSUM || 0) - (itemStock.RESERVEDFORIM || 0);

                        if(accessItemQty < 10){
                            if (accessItemQty < element.QUANT) {
                                newMath[i].redBorder = true;
                                newMath[i].inStock = accessItemQty;
                            }
                        }
                    }  

                    const newData = {basket: {
                        math: [],
                        totsum: basketRecoil.basket.totsum,
                        totvolume: basketRecoil.basket.totvolume,
                        totweight: basketRecoil.basket.totweight
                    }};
                    newData.basket.math = newMath.sort((a, b) => a.inStock - b.inStock);
                    setBasketRecoil(newData);

                    setTitelInfoDialog('');
                    setContentTextInfoDialog(`${l('The quantity of the product is not enough in stock')}.`);
                    setOpenSuccessDialog(true);
                    return;
                }
                else{
                    setTitelInfoDialog(null);
                    setContentTextInfoDialog(null)
                }

                clearBasket();
                setOpenSuccessDialog(true);
                setShowRecommendations(true);
            } catch (error) {
                console.error('Error deleteBasket >> ', error);
                clearBasket();
                setOpenSuccessDialog(true);
            }
        };

        fetcData();
    };

    const clearBasket = () => {
        const newBasket = {
            basket: {math: [], totsum: 0, totvolume: 0, totweight: 0},
        };
        setBasketRecoil(newBasket);
    };

    const columns = [
        { id: 'QTARTCODE', label: l('Code'), minWidth: 50, type: CellType.STRING },
        { id: 'FNAME', label: l('Name'), minWidth: 150, type: CellType.PRODUCT_NAME },
        { id: 'FIMG', label: l('Image'), minWidth: 60, type: CellType.IMG, align: 'center' },
        { id: 'inStock', label: l('Availability'), minWidth: 100, type: CellType.IS_INSTOCK_MORE_THAN_NUM, align: 'center' },
        { id: 'FPRICE1', label: `${l('Price')}` + (appCompno === '2' ? `,${currencySignPrice1}` : ''), minWidth: 84, type: CellType.PRISE_WITH_DISCOUNT, align: 'center' },
        { id: 'QUANT', label: l('Quantity'), minWidth: 68, type: CellType.EDITABLE_NUMBER },
        { id: 'FMINQTY', label: l('Min. order'), minWidth: 56, type: CellType.MIN_QTY, align: 'center', headerTooltip: l('Min order quantity tooltip') },
        { id: 'FPACQTY', label: l('Pack'), minWidth: 44, type: CellType.PACK_QTY, align: 'center', headerTooltip: l('Quantity in a package') },
        { id: 'ITEMSUM', label: l('Sum'), minWidth: 68, type: CellType.PRODUCT_SUM },
        { id: 'Action', label: l('Action'), minWidth: 56, type: CellType.ACTIONS, align: 'center' },
    ];

    const debounceRefEditableNumbers = React.useRef(null);
    const handelAction = (event, type, row) => {

        let { value } = event.target;

        switch (type) {
            case CellType.EDITABLE_NUMBER:

                if(value === '0' || value === '') {
                    value = 1;
                }

                if (debounceRefEditableNumbers.current) {
                    clearTimeout(debounceRefEditableNumbers.current);
                }

                debounceRefEditableNumbers.current = setTimeout(() => {
                    const newRow = { ...row };

                    const effectiveQty = getEffectiveOrderQty(newRow);
                    let quant = parseInt(value, 10) || 0;

                    if (effectiveQty > 1 && quant % effectiveQty !== 0) {
                        quant = Math.max(effectiveQty, Math.round(quant / effectiveQty) * effectiveQty);
                    }

                    newRow.QUANT = quant;
                    const discount = newRow.FDISCOUNT || 0;

                    let pricewithoutDiscount = Math.round(newRow.FPRICE1 * newRow.QUANT * 100) / 100;
                    let priceWithDiscount = Math.round((pricewithoutDiscount - (pricewithoutDiscount * discount / 100)) * 100) / 100;
                    newRow.ITEMSUM = priceWithDiscount;

                    // let priceWithDiscount = Math.round((newRow.FPRICE1 - (newRow.FPRICE1 * discount / 100)) * 100) / 100;
                    // priceWithDiscount = Math.round(priceWithDiscount * newRow.QUANT * 100) / 100;
                    // newRow.ITEMSUM = priceWithDiscount;

                    newRow.SUMFWEIGHT = Math.round(newRow.FWEIGHT * newRow.QUANT * 1000) / 1000;
                    newRow.SUMFVOLUME = Math.round(newRow.FVOLUME * newRow.QUANT * 100000) / 100000;



                    if (user) {
                        try {
                            const fetcData = async () => {

                                const newBasket = await basketService.updateBasket(newRow);

                                setOpenAlertDialog(!newBasket?.math?.find((item) => item.QUANT < item.FMINQTY))

                                setBasketRecoil({ basket: newBasket || {} });
                            };

                            fetcData();

                        } catch (error) {
                            console.error('Error update Cart :>> ', error);
                        }
                    }

                    event.target.value = quant.toString();

                    event.target.select();
                }, 500); // Затримка у 500 мс

                break;

                case 'DISMISS_GHOST':
                    setDismissedArtcodes(prev => new Set([...prev, row.QTARTCODE]));
                    break;

                case CellType.ACTIONS: {
                    if (row._ghost) {
                        if (user) {
                            setDismissedArtcodes(prev => new Set([...prev, row.QTARTCODE]));
                            (async () => {
                                const newBasket = await basketService.updateBasket({ ...row, Recommended: 1 });
                                setOpenAlertDialog(!newBasket?.math?.find(item => item.QUANT < item.FMINQTY));
                                setBasketRecoil({ basket: newBasket || {} });
                            })().catch(err => console.error('Error adding recommendation:', err));
                        }
                        break;
                    }

                    const newRow = { ...row };

                    newRow.QUANT = 0;

                    if (user) {
                        try {
                            const fetcData = async () => {

                                setBasketRecoil({});

                                const newBasket = await basketService.updateBasket(newRow);

                                setOpenAlertDialog(!newBasket?.math?.find((item) => item.QUANT < item.FMINQTY))

                                setBasketRecoil({ basket: newBasket || {} });
                            };

                            fetcData();

                        } catch (error) {
                            console.error('Error update Cart :>> ', error);
                        }
                    }

                    break;
                }

                case CellType.PRODUCT_NAME:
                const url = compDetailUrl + row.FINFOPAGE;

                setOpenDetail(true);
                setDetailUrl(url);
                break;

            default:
                break;
        }
    }

    return (
        <>
            <Box display="flex" flexDirection='column' sx={{ mt: 2 }} gap={2}>
                <Box display="flex" gap={2} justifyContent="space-between">
                    <Box display="flex" alignItems={'center'} gap={2} flexWrap={'wrap'}
                    sx={(theme) => ({
                        [theme.breakpoints.down('md')]: {
                            alignItems: 'start',
                            gap: 0
                            },
                    })}
                    >
                        <Typography component="h2" variant="h5">
                            {l('Confirm the shipment of the Order')}
                        </Typography>

                        <Box display="flex" alignItems="center" gap={3} flexWrap={'wrap'}>
                            <Divider sx={{display: {xs: 'none', md: 'block'}}} orientation="vertical" flexItem />
                            <Typography component="h6" variant="h6">
                                {l('Total')}:
                            </Typography>

                            <Typography display="flex" alignItems="center" component="h6" variant="h6">
                                <Box style={{ color: "green" }}>
                                    {basketRecoil?.basket?.totsum || 0}{currencySignPrice1}
                                </Box>
                                {appCompno === '1' ? 
                                <Tooltip title={l('Exchange rate') + `: ${rate} ₴`}>
                                    <Box style={{ color: "green", marginLeft: '5px' }}>
                                        {`(~${Math.round(rate * basketRecoil?.basket?.totsum) || 0}₴)`}
                                    </Box>
                                </Tooltip> : ''}
                            </Typography>

                            <Typography display="flex" alignItems="center" component="h6" variant="h6">
                                <ScaleIcon />
                                <span style={{ color: "#dc3545", marginLeft: 5 }}>
                                    {basketRecoil?.basket?.totweight}{l('Kg')}
                                </span>
                            </Typography>

                            <Typography display="flex" alignItems="center" component="h6" variant="h6">
                                <WbIridescentIcon />
                                <span style={{ color: "#007bff", marginLeft: 5 }}>
                                    {basketRecoil?.basket?.totvolume}{l('m³')}
                                </span>
                            </Typography>
                        </Box>
                    </Box>

                    <Box display={'flex'} gap={1}
                    sx={(theme) => ({
                        // Стилі ТІЛЬКИ для екранів менше 1200px
                        alignItems: 'center',
                        [theme.breakpoints.down('lg')]: {
                        flexWrap: 'wrap',
                        flexDirection: 'column-reverse',
                        alignItems: 'end',
                        },
                    })}
                    >
                        <Box ref={recSwitchRef} display="inline-flex">
                            <Tooltip title={l('Recommendations switch info')}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={showRecommendations}
                                            onChange={e => setShowRecommendations(e.target.checked)}
                                            size="small"
                                            color="primary"
                                        />
                                    }
                                    label={l('Recommendations')}
                                    sx={{ mr: 1, whiteSpace: 'nowrap' }}
                                />
                            </Tooltip>
                            </Box>
                        <Popover
                            open={Boolean(recInfoAnchorEl)}
                            anchorEl={recInfoAnchorEl}
                            onClose={handleRecInfoClose}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                            disableRestoreFocus
                            PaperProps={{
                                sx: {
                                    mt: 1.5,
                                    maxWidth: 300,
                                    p: 2,
                                    overflow: 'visible',
                                    '&::before': {
                                        content: '""',
                                        display: 'block',
                                        position: 'absolute',
                                        top: -8,
                                        left: '50%',
                                        transform: 'translateX(-50%)',
                                        width: 0,
                                        height: 0,
                                        borderLeft: '8px solid transparent',
                                        borderRight: '8px solid transparent',
                                        borderBottom: '8px solid white',
                                    },
                                },
                            }}
                        >
                            <Typography variant="body2" sx={{ mb: 1.5, whiteSpace: 'pre-line' }}>
                                {l('Recommendations switch info')}
                            </Typography>
                            <Box display="flex" justifyContent="flex-end">
                                <Button size="small" variant="contained" onClick={handleRecInfoClose}>
                                    {l('Got it')}
                                </Button>
                            </Box>
                        </Popover>
                        <Box display={'flex'} gap={2}
                        sx={(theme) => ({
                            // Стилі ТІЛЬКИ для екранів менше 1200px
                            [theme.breakpoints.down('md')]: {
                            flexWrap: 'wrap',
                            flexDirection: 'column',
                            alignItems: 'end',
                            },
                        })}
                        >
                            <Button disabled={hasInvalidMultiplicity || (isCheckQuant === '1' ? (basketRecoil?.basket?.totsum < orderlimit || !basketRecoil?.basket?.math?.length || !openAlertDialog) : basketRecoil?.basket?.math?.length ? false : true)}
                                sx={{
                                    "&.Mui-disabled": {
                                        color: "white",
                                    }
                                }}
                                variant="contained"
                                autoFocus
                                color="success"
                                onClick={handleConfirm}
                            >
                                {l('Send')}
                            </Button> 
                            <Button
                                sx={{
                                    "&.Mui-disabled": {
                                        color: "white",
                                    },
                                    minWidth: '192px'
                                }}
                                disabled={!basketRecoil?.basket?.math?.length}
                                variant="contained"
                                autoFocus
                                color="error"
                                onClick={() => setOpenConfirmDialog(true)}>
                                {l('Clear basket')}
                            </Button>
                        </Box>
                    </Box>

                </Box>
                {/* <Alert
                    severity={showRecommendations ? 'info' : 'warning'}
                    action={
                        <Switch
                                        checked={showRecommendations}
                                        onChange={e => setShowRecommendations(e.target.checked)}
                                        size="small"
                                        color="primary"
                                    />
                    }
                >
                    {showRecommendations
                        ? l('Recommended products are highlighted in blue. They are NOT yet in your basket but may complement your order. You can add or dismiss them.')
                        : l('Recommended products are currently hidden. Click SHOW to see suggestions based on the items in your cart.')}
                </Alert> */}
                {basketRecoil?.basket?.totsum < orderlimit ? <Alert severity="error">{l('The order amount must be at least ') + currencySignPrice1 + orderlimit}</Alert> : ''}
                {isCheckQuant === '1' ? (!openAlertDialog && basketRecoil?.basket?.math?.length) ? <Alert severity="error">{l('The number of selected products is less than the minimum')}</Alert> : '' : ''}
                {hasInvalidMultiplicity ? <Alert severity="error">{l('The quantity of some products is not a multiple of the order multiplicity')}</Alert> : ''}

                <Box>
                    <TableData
                        columns={columns}
                        rows={displayRows}
                        cellBtnActionByType={handelAction}
                        rowsPerPageV = {500}
                        tableEmptyText={l('Cart is empty')}
                        isBasket={true}
                    />
                </Box>
                
                <Box width='100%'>
                    <TextField
                        fullWidth
                        onChange={(e) => setComment(e.target.value)}
                        sx={{ backgroundColor: 'white' }}
                        multiline
                        rows={1}
                        label={`${l("Your comment")}...`}
                        variant="outlined"
                        inputProps={{ maxLength: 125 }}
                    />
                    <Typography color='#515151' marginTop={1} component="p" variant="p">
                        {l('Number of characters')} {comment.length} {l('of')} 125
                    </Typography>
                </Box>
            </Box>
            <ConfirmDialog
                handleConfirm={handleDelete}
                open={openConfirmDialog}
                handleClose={() => setOpenConfirmDialog(false)}
                title={`⚠️ ${l('Clear basket')}`}
                contentText={`${l('Are you sure that you want to clear the basket, all products that were selected will be deleted')}!`}
                denyButtonText={l('Cancel')}
                confirmButtonText={l('Agree')}
            />
            <InfoDialog
                handleConfirm={() => setOpenSuccessDialog(false)}
                open={openSuccessDialog}
                handleClose={() => setOpenSuccessDialog(false)}
                title={titelInfoDialog ||`${l('Information')}`}
                contentText={contentTextInfoDialog || `${l('Thank you for the order')}! ${l('Our manager will contact you as soon as possible')}.`}
                confirmButtonText={l('OK')}
            />
            <DetailScreen isOpened={openDetail} setOpenDetail={setOpenDetail} detailUrl={detailUrl} />
        </>
    );
}

export default BasketPage;